/**
 * The run's reconciliation controls, read back out of the produced workbook's
 * Controls sheet — every control with its OK/FAILED verdict, the itemised
 * entity lines behind the failures, and the agent-assisted fix loop:
 *
 *   instruction (operator's words)
 *     → plan (the agent proposes deterministic edit operations — nothing applied)
 *       → apply (a NEW corrected artifact, lineage back to the original)
 *         → re-process (the same tool, through the ordinary run path)
 *
 * The review checkpoint between plan and apply is deliberate: the operator
 * always sees exactly which cells/rows change before anything does.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  ApiError,
  api,
  type EditOperation,
  type ReviseApplied,
  type RevisePlan,
  type RunControl,
  type RunControlEntityLine,
  type RunControlsReport,
} from "../lib/api";
import { useApiQuery } from "../hooks/useApiQuery";
import { useEditMode } from "../hooks/useCustomer";
import { useToast } from "../hooks/useToast";
import { aiEnabled } from "../lib/states";
import { SectionLabel } from "../components/ui/SectionLabel";

const LINE_COLOR: Record<RunControlEntityLine["kind"], string> = {
  move: "text.secondary",
  dropped: "error.main",
  unknown: "warning.main",
  warning: "text.secondary",
};

export function RunControlsPanel({ jobId }: { jobId: string }) {
  const query = useApiQuery(["controls", jobId], (signal) =>
    api.jobControls(jobId, signal),
  );

  const report = query.data;
  if (!report?.available) return null;
  return <ControlsCard report={report} />;
}

/** How this control moved versus the run the revision was answering. */
function deltaOf(control: RunControl): "cleared" | "detailChanged" | "unchanged" | "regressed" | null {
  if (control.previous_ok == null) return null;
  if (!control.previous_ok && control.ok) return "cleared";
  if (control.previous_ok && !control.ok) return "regressed";
  if (!control.ok) {
    return control.detail !== (control.previous_detail ?? "") ? "detailChanged" : "unchanged";
  }
  return null; // ok before, ok now — nothing worth a badge
}

const DELTA_COLOR = {
  cleared: "success",
  detailChanged: "info",
  unchanged: "default",
  regressed: "error",
} as const;

function ControlsCard({ report }: { report: RunControlsReport }) {
  const { t } = useTranslation();
  const controls = report.controls ?? [];
  const failed = controls.filter((c) => !c.ok).length;
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  async function downloadPdf() {
    setPdfBusy(true);
    setPdfError(null);
    try {
      await api.downloadControlsPdf(report.job_id);
    } catch (error) {
      setPdfError(
        error instanceof ApiError ? error.message : t("controls.downloadPdfFailed"),
      );
    } finally {
      setPdfBusy(false);
    }
  }

  return (
    <Card>
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <SectionLabel>{t("controls.title")}</SectionLabel>
          {/* The workbook holds the same facts, in the engine's own wording.
              This hands a controller something they can forward to Finance. */}
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={downloadPdf}
            disabled={pdfBusy}
            title={t("controls.downloadPdfHint")}
          >
            {pdfBusy ? t("controls.downloadingPdf") : t("controls.downloadPdf")}
          </Button>
        </Stack>
        {pdfError && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setPdfError(null)}>
            {pdfError}
          </Alert>
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {report.run_line}
          {report.meta?.length ? ` · ${report.meta.join(" · ")}` : ""}
        </Typography>
        {report.input_display_name && (
          <Typography variant="caption" color="text.secondary" display="block">
            {t("controls.inputFile")}: <strong>{report.input_display_name}</strong>
          </Typography>
        )}
        {/* This run answers a fix: say plainly how the controls moved. */}
        {report.previous && (
          <Alert
            severity={failed < report.previous.failed_count ? "success" : "info"}
            sx={{ mt: 1 }}
          >
            {t("controls.comparedTo", {
              before: report.previous.failed_count,
              after: failed,
            })}
          </Alert>
        )}

        <Stack spacing={0} sx={{ mt: 1.5 }}>
          {controls.map((control, index) => (
            <Stack
              key={index}
              direction="row"
              spacing={1.5}
              alignItems="flex-start"
              sx={{
                py: 0.75,
                borderBottom: 1,
                borderColor: "divider",
                "&:last-child": { border: 0 },
              }}
            >
              <Chip
                size="small"
                icon={control.ok ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
                label={control.ok ? t("controls.ok") : t("controls.failed")}
                color={control.ok ? "success" : "error"}
                variant={control.ok ? "outlined" : "filled"}
                sx={{ minWidth: 92, fontWeight: 600 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2">{control.name}</Typography>
                  {(() => {
                    const delta = deltaOf(control);
                    return delta ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={DELTA_COLOR[delta]}
                        label={t(`controls.delta.${delta}`)}
                        sx={{ height: 20 }}
                      />
                    ) : null;
                  })()}
                </Stack>
                {control.detail && (
                  <Typography variant="caption" color={control.ok ? "text.secondary" : "error.main"}>
                    {control.detail}
                  </Typography>
                )}
                {/* the moved detail is the PROOF the corrected file was
                    processed — show it right under the current one */}
                {deltaOf(control) === "detailChanged" && control.previous_detail && (
                  <Typography variant="caption" color="text.disabled" display="block">
                    {t("controls.previously")}: {control.previous_detail}
                  </Typography>
                )}
              </Box>
            </Stack>
          ))}
        </Stack>

        {(report.entities ?? []).map((entity) => (
          <EntityDetail key={entity.header} header={entity.header} lines={entity.lines} />
        ))}

        {(report.extras ?? []).length > 0 && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            {(report.extras ?? []).join(" — ")}
          </Alert>
        )}

        {report.input_artifact_id && report.tool && (
          <FixSection
            inputArtifactId={report.input_artifact_id}
            contextArtifactId={report.artifact_id}
            tool={report.tool}
            anyFailed={failed > 0}
          />
        )}
      </CardContent>
    </Card>
  );
}

function EntityDetail({
  header,
  lines,
}: {
  header: string;
  lines: RunControlEntityLine[];
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const flagged = lines.filter((l) => l.kind !== "move").length;

  return (
    <Box sx={{ mt: 1 }}>
      <Button
        size="small"
        color="inherit"
        onClick={() => setOpen(!open)}
        startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        aria-expanded={open}
        sx={{ color: "text.secondary", textAlign: "left" }}
      >
        {header}
        {flagged > 0 ? ` · ${t("controls.flaggedLines", { count: flagged })}` : ""}
      </Button>
      <Collapse in={open} unmountOnExit>
        <Stack spacing={0.25} sx={{ pl: 4, pb: 1 }}>
          {lines.map((line, index) => (
            <Typography
              key={index}
              variant="caption"
              className="num"
              sx={{ color: LINE_COLOR[line.kind], whiteSpace: "pre-wrap" }}
            >
              {line.text}
            </Typography>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

function opTarget(op: EditOperation): string {
  const sheet = op.sheet ? `${op.sheet} · ` : "";
  if (op.action === "set_cell") return `${sheet}${op.cell} = ${String(op.value)}`;
  if (op.action === "delete_rows") return `${sheet}${(op.rows ?? []).join(", ")}`;
  return `${sheet}"${op.find}" → "${String(op.replace)}"${op.column ? ` (${op.column})` : ""}`;
}

function FixSection({
  inputArtifactId,
  contextArtifactId,
  tool,
  anyFailed,
}: {
  inputArtifactId: string;
  contextArtifactId?: string;
  tool: string;
  anyFailed: boolean;
}) {
  const { t } = useTranslation();
  const edit = useEditMode();
  const navigate = useNavigate();
  const [instruction, setInstruction] = useState("");
  const [plan, setPlan] = useState<RevisePlan | null>(null);
  const [applied, setApplied] = useState<ReviseApplied | null>(null);
  const [busy, setBusy] = useState<"plan" | "apply" | "rerun" | null>(null);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const toast = useToast();
  // proposing a plan asks a model; APPLYING one does not, so only this half
  // follows the switch — a plan already reviewed stays applicable.
  const ai = aiEnabled();

  async function propose() {
    if (!ai) {
      toast(t("common.aiDisabled"), "info");
      return;
    }
    setBusy("plan");
    setFailure(null);
    setPlan(null);
    setApplied(null);
    try {
      setPlan(
        await api.revisePlan(inputArtifactId, instruction.trim(), contextArtifactId, tool),
      );
    } catch (err) {
      setFailure(err as ApiError);
    } finally {
      setBusy(null);
    }
  }

  async function apply() {
    if (!plan) return;
    setBusy("apply");
    setFailure(null);
    try {
      setApplied(await api.reviseApply(inputArtifactId, plan.operations));
    } catch (err) {
      setFailure(err as ApiError);
    } finally {
      setBusy(null);
    }
  }

  async function reprocess() {
    if (!applied) return;
    setBusy("rerun");
    setFailure(null);
    try {
      const started = await api.runTool({ tool, artifact_id: applied.artifact_id });
      void navigate(`/runs/${encodeURIComponent(started.job_id)}`);
    } catch (err) {
      setFailure(err as ApiError);
      setBusy(null);
    }
  }

  return (
    <Box sx={{ mt: 2.5, pt: 2, borderTop: 1, borderColor: "divider" }}>
      <Typography variant="subtitle2" fontWeight={700}>
        {t("controls.fixTitle")}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {anyFailed ? t("controls.fixHintFailed") : t("controls.fixHint")}
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="flex-start">
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder={t("controls.instructionPlaceholder")}
          disabled={busy !== null}
        />
        <Button
          variant="outlined"
          startIcon={<AutoFixHighIcon />}
          onClick={() => void propose()}
          disabled={busy !== null || !edit.canEdit || instruction.trim().length === 0}
          sx={{
            flexShrink: 0,
            mt: { sm: 0.25 },
            ...(ai
              ? {}
              : { color: "text.disabled", borderColor: "action.disabled" }),
          }}
        >
          {busy === "plan" ? t("controls.proposing") : t("controls.propose")}
        </Button>
      </Stack>

      {failure && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {failure.message}
        </Alert>
      )}

      {plan && !applied && (
        <Box sx={{ mt: 1.5 }}>
          <Alert severity={plan.operations.length > 0 ? "info" : "warning"}>
            {plan.summary || t("controls.emptyPlan")}
          </Alert>
          {/* the agent's own caveat: these edits will NOT clear the failed
              controls — read it before applying */}
          {plan.warning && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {plan.warning}
            </Alert>
          )}
          {plan.operations.length > 0 && (
            <>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("controls.opAction")}</TableCell>
                      <TableCell>{t("controls.opTarget")}</TableCell>
                      <TableCell>{t("controls.opReason")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {plan.operations.map((op, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {t(`controls.op.${op.action}`)}
                        </TableCell>
                        <TableCell className="num">{opTarget(op)}</TableCell>
                        <TableCell>{op.reason ?? ""}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              <Button
                variant="contained"
                sx={{ mt: 1.5 }}
                onClick={() => void apply()}
                disabled={busy !== null || !edit.canEdit}
              >
                {busy === "apply" ? t("controls.applying") : t("controls.apply")}
              </Button>
            </>
          )}
        </Box>
      )}

      {applied && (
        <Alert
          severity="success"
          sx={{ mt: 1.5 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<PlayArrowIcon />}
              onClick={() => void reprocess()}
              disabled={busy !== null}
            >
              {busy === "rerun" ? t("controls.rerunning") : t("controls.rerun")}
            </Button>
          }
        >
          {t("controls.appliedMessage", {
            name: applied.display_name,
            count: applied.changed_total,
          })}{" "}
          <Link
            component="button"
            type="button"
            onClick={() =>
              void api.downloadExcel(applied.artifact_id, applied.display_name)
            }
            underline="hover"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}
          >
            <DownloadIcon sx={{ fontSize: 16 }} /> {t("files.download")}
          </Link>
        </Alert>
      )}
    </Box>
  );
}
